//***********************************************************
// components/Tabs/AgoraStandardMeeting.tsx
// 通常会議専用のAgoraコンポーネント（招待ユーザー対応版）
//***********************************************************
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import AgoraRTC, { IAgoraRTCClient } from 'agora-rtc-sdk-ng';
import { Button, Input, Typography, Space, message, Card } from 'antd';
import { doc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { RemoteUser, Participant, MeetingType, AGORA_CONFIG } from './web_parts/AgoraTypes';
import { useAuth } from '../../context/AuthContext';
import { useWindowSize, parseUid, fetchMeetingData, fetchInitialData, calculateOptimalVideoSize, getParticipantName, getNonParticipants, playRemoteVideo, getCollectionName, markChannelClosedInFirestore, debugLog} from './web_parts/AgoraBase';

const { Title } = Typography;

/**
 * 通常会議専用のAgoraコンポーネント（招待ユーザー対応）
 * 認証済みユーザーと招待ユーザー（ログインなし）の両方に対応
 */
const AgoraStandardMeeting: React.FC = () => {
  // URLパラメータの取得
  const { channelId } = useParams<{ channelId: string }>();
  const [searchParams] = useSearchParams();
  const isTimingDebugMode =
    searchParams.get('debug') === '1' ||
    searchParams.get('timingDebug') === '1' ||
    process.env.REACT_APP_TIMING_DEBUG === 'true' ||
    (typeof window !== 'undefined' && window.localStorage.getItem('caretablet:timingDebug') === '1');
  
  // 招待ユーザー用のパラメータ
  const isInvitedUser = searchParams.has('invited'); // ?invited=true
  const invitedUserName = searchParams.get('name'); // ?name=山田太郎
  
  // 認証情報の取得（招待ユーザーの場合は認証不要）
  const authContext = useAuth();
  const { currentUser } = authContext || {};
  
  // 基本状態管理
  const [name, setName] = useState<string>('');
  const [uid, setUid] = useState<number | null>(null);
  const [joined, setJoined] = useState<boolean>(false);
  const [isJoining, setIsJoining] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [cameraDeviceIds, setCameraDeviceIds] = useState<string[]>([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState<number>(0);
  
  // 招待ユーザー用の状態
  const [showNameInput, setShowNameInput] = useState<boolean>(false);
  const [isInvitedUserMode, setIsInvitedUserMode] = useState<boolean>(false);
  
  // 再入室防止の管理
  const [autoJoinAttempted, setAutoJoinAttempted] = useState<boolean>(false);

  //初期データ読み込み完了フラグ
  const [initialDataLoaded, setInitialDataLoaded] = useState<boolean>(false);

  // 会議・参加者情報
  const [meetingName, setMeetingName] = useState<string>('');
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [joinTiming, setJoinTiming] = useState<{
    status: 'idle' | 'running' | 'success' | 'error';
    totalElapsedMs?: number;
    joinMs?: number;
    invitedRecordMs?: number;
    tracksMs?: number;
    camerasMs?: number;
    publishMs?: number;
  }>({ status: 'idle' });
  
  // リモートユーザー関連
  const [remoteUsers, setRemoteUsers] = useState<RemoteUser[]>([]);
  const [speakingUser, setSpeakingUser] = useState<string | null>(null);
  const [remoteAudioStates, setRemoteAudioStates] = useState<{ [uid: string]: boolean }>({});
  const [remoteVideoStates, setRemoteVideoStates] = useState<{ [uid: string]: boolean }>({});
  
  // 退室状態管理
  const [hasLeft, setHasLeft] = useState(false);
  const [leftReason, setLeftReason] = useState<'manual' | 'error'>('manual');
  
  // DOM参照
  const localVideoRef = useRef<HTMLDivElement>(null);
  const mainVideoRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const clientRef = useRef<IAgoraRTCClient | null>(null);
  const localTracks = useRef<{ videoTrack: any; audioTrack: any } | null>(null);
  const remoteVideoRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  
  // レスポンシブデザイン
  const { width, height, isMobilePortrait, isWideLayout } = useWindowSize();
  const [mainHeight, setMainHeight] = useState('100vh');

  const isIgnorableLeaveError = (error: unknown): boolean => {
    const text = String(error ?? "").toLowerCase();
    return (
      text.includes("not published") ||
      text.includes("stream is not published") ||
      text.includes("not in channel") ||
      text.includes("connection state") ||
      text.includes("disconnected") ||
      text.includes("invalid operation")
    );
  };
  
  // カメラオフアイコンの読み込み
  const cameraOffIcon = require("../../assets/images/icon_camera_off.png");

  // 🆕 追加: レスポンシブ対応のアイコンサイズ計算
  const getCameraOffIconSize = (isMainDisplay: boolean = false) => {

    if (isMobilePortrait) {
      // モバイル縦向き
      return isMainDisplay ? { width: '64px', height: '64px' } : { width: '32px', height: '32px' };
    } else if (width < 768) {
      // モバイル横向き
      return isMainDisplay ? { width: '80px', height: '80px' } : { width: '32px', height: '32px' };
    } else {
      // デスクトップ・タブレット
      return isMainDisplay ? { width: '128px', height: '128px' } : { width: '64px', height: '64px' };
    }
    
  };

  /**
   * 招待ユーザーモードの判定と初期化
   */
  useEffect(() => {
    const initializeUserMode = () => {
      if (isInvitedUser) {
        // 招待ユーザーモード
        setIsInvitedUserMode(true);
        
        if (invitedUserName) {
          // URLから名前が渡されている場合
          setName(invitedUserName);
          setShowNameInput(false);
        } else {
          // 名前入力が必要な場合
          setShowNameInput(true);
        }
        
        // 招待ユーザー用のランダムUID生成（確実に8桁）
        const randomUid = Math.floor(10000000 + Math.random() * 90000000);
        setUid(randomUid);
        setInitialDataLoaded(true);
        
        debugLog('招待ユーザーモードで初期化', { 
          name: invitedUserName || '未設定', 
          uid: randomUid 
        });
        
      } else {
        // 通常ユーザーモード（認証済みユーザー）
        setIsInvitedUserMode(false);
        setShowNameInput(false);
        
        // 既存の初期データ取得処理を実行
        loadInitialDataForAuthenticatedUser();
      }
    };

    initializeUserMode();
  }, [isInvitedUser, invitedUserName, channelId]);

  /**
   * 認証済みユーザー用の初期データ取得
   * URLパラメータ(name/uid)をFirestoreデータのフォールバックとして使用する。
   * user-web等の別ドメインから開かれた場合、Firestoreアクセスが失敗する可能性があるため。
   */
  const loadInitialDataForAuthenticatedUser = async () => {
    if (!channelId) return;
    
    // URLパラメータからフォールバック用の name/uid を取得
    const urlName = searchParams.get('name');
    const urlUid = searchParams.get('uid');
    
    try {
      const { name: initialName, uid: initialUid } = await fetchInitialData(
        channelId,
        getCollectionName(MeetingType.STANDARD),
        urlName,  // URLパラメータをフォールバックとして渡す
        urlUid    // URLパラメータをフォールバックとして渡す
      );
      
      setName(initialName);
      setUid(initialUid);
      setInitialDataLoaded(true);
      
      debugLog('認証ユーザーの初期データ設定完了', { 
        name: initialName, 
        uid: initialUid 
      });
    } catch (error) {
      console.error('初期データ取得失敗:', error);
      // エラー時はURLパラメータを使用し、なければフォールバック
      const fallbackUid = urlUid ? parseInt(urlUid, 10) : Math.floor(Math.random() * 1000000);
      setName(urlName || 'ゲストユーザー');
      setUid(isNaN(fallbackUid) ? Math.floor(Math.random() * 1000000) : fallbackUid);
      setInitialDataLoaded(true);
    }
  };

  /**
   * 招待ユーザーの名前入力完了処理
   */
  const handleNameSubmit = () => {
    if (isJoining) {
      return;
    }

    if (!name.trim()) {
      message.error('名前を入力してください');
      return;
    }
    
    setShowNameInput(false);
    debugLog('招待ユーザーの名前入力完了', { name });
  };

  /**
   * 招待ユーザーのFirestoreデータを作成
   */
  const createInvitedUserRecord = async (channelId: string, userName: string, uid: number) => {
    if (!isInvitedUserMode) return; // 招待ユーザーモードの場合のみ実行
    
    try {
      const recordStartMs = performance.now();
      const logRecordStep = (step: string, stepStartMs?: number) => {
        const totalElapsedMs = Math.round(performance.now() - recordStartMs);
        const stepElapsedMs = stepStartMs == null ? undefined : Math.round(performance.now() - stepStartMs);
        debugLog('[join-profiler][standard][invitedRecord]', {
          step,
          totalElapsedMs,
          stepElapsedMs,
          channelId,
          uid,
        });
      };
      logRecordStep('start');
      console.log('招待ユーザーのFirestoreレコード作成開始');
      
      // 既存レコードの確認（重複防止）
      const existsCheckStepStart = performance.now();
      const existingQuery = query(
        collection(db, "agoraitems"),
        where("channelID", "==", channelId),
        where("uid", "==", uid)
      );
      const existingSnapshot = await getDocs(existingQuery);
      logRecordStep('query_existing_by_channel_uid:done', existsCheckStepStart);
      
      if (!existingSnapshot.empty) {
        console.log('既存のレコードが見つかりました。作成をスキップします。');
        logRecordStep('skip_existing_record');
        return;
      }

      // serialNumber=00000000の既存ドキュメントから最大番号を取得
      const serialQueryStepStart = performance.now();
      const agoraQuery = query(
        collection(db, "agoraitems"),
        where("serialNumber", "==", "00000000")
      );
      const querySnapshot = await getDocs(agoraQuery);
      logRecordStep('query_by_serialNumber:done', serialQueryStepStart);

      const existingDocs = querySnapshot.docs.map((doc) => doc.id);
      const maxIndex = existingDocs
        .map((id) => parseInt(id.split("_")[1], 10))
        .filter((num) => !isNaN(num))
        .reduce((max, num) => Math.max(max, num), 0);

      // みまもり会議と同じ命名規則を使用
      const invitedDocId = `00000000_${maxIndex + 1}`;
      
      // 既存の会議情報を取得（開催者のデータを参照）
      const meetingQueryStepStart = performance.now();
      const meetingQuery = query(
        collection(db, "agoraitems"),
        where("channelID", "==", channelId),
        where("serialNumber", "==", "00000000") // 主催者のレコード
      );
      const meetingSnapshot = await getDocs(meetingQuery);
      logRecordStep('query_meeting_host_record:done', meetingQueryStepStart);
      
      let meetingData = null;
      if (!meetingSnapshot.empty) {
        meetingData = meetingSnapshot.docs[0].data();
      }

      // 基本的に開催者データと同じ + ゲスト用の特定フィールドのみ変更
      const invitedUserData = {
        meetingName: meetingData?.meetingName || "テレビ会議",
        starttime: meetingData?.starttime || Date.now(),
        durationminute: meetingData?.durationminute || 60,
        channelID: channelId,
        updateTime: Date.now(),
        regPerson: meetingData?.regPerson || "開催者", // 開催者のデータと同じ名前
        serialNumber: "00000000",
        name: userName, // ゲスト参加者が入力した名前
        uid: uid, // 8桁のランダム数値
        deleted: false,
        ictabUid: "", // 空文字
      };

      const writeStepStart = performance.now();
      await setDoc(doc(db, "agoraitems", invitedDocId), invitedUserData);
      logRecordStep('setDoc_invited_record:done', writeStepStart);
      
      console.log('招待ユーザーのFirestoreレコード作成完了:', invitedDocId);
      message.success('会議データを作成しました');
      logRecordStep('success');
      
    } catch (error) {
      console.error('招待ユーザーのFirestoreレコード作成エラー:', error);
      debugLog('[join-profiler][standard][invitedRecord]', {
        step: 'error',
        channelId,
        uid,
        error: String(error),
      });
      // エラーが発生しても会議参加は継続（Firestoreデータは必須ではないため）
      console.warn('Firestoreデータ作成に失敗しましたが、会議には参加できます');
    }
  };

  /**
   * 自動入室処理
   */
  useEffect(() => {
    // 名前入力待ちの場合は自動入室しない
    if (showNameInput) return;
    
    if (initialDataLoaded && uid !== null && !autoJoinAttempted && !isJoining && name.trim()) {
      debugLog('自動入室を実行（1回限り）', { name, uid, isInvitedUserMode });
      
      const timer = setTimeout(() => {
        joinMeeting();
        setAutoJoinAttempted(true);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [initialDataLoaded, uid, name, autoJoinAttempted, showNameInput, isJoining]);

  /**
   * 会議情報と参加者情報の取得
   */
  useEffect(() => {
    const loadMeetingData = async () => {
      if (!channelId || uid === null) return;

      try {
        const { meetingName: fetchedMeetingName, participants: fetchedParticipants } = 
          await fetchMeetingData(channelId, getCollectionName(MeetingType.STANDARD), uid);
        
        setMeetingName(fetchedMeetingName);
        setParticipants(fetchedParticipants);
        
        debugLog('会議データ取得完了', { 
          meetingName: fetchedMeetingName, 
          participantCount: fetchedParticipants.length 
        });
      } catch (error) {
        console.error('会議データ取得失敗:', error);
      }
    };

    loadMeetingData();
  }, [channelId, uid, remoteUsers]);

  /**
   * コンポーネントのクリーンアップ - beforeunload対応版
   */
  useEffect(() => {
    return () => {
      if (clientRef.current && joined) {
        debugLog('コンポーネントクリーンアップ: 会議から退出');
        // 強制退室フラグをtrueに設定
        leaveMeeting(true);
      }
    };
  }, [channelId, joined]);

  /**
   * ローカルビデオの再生
   */
  useEffect(() => {
    if (joined && localVideoRef.current && localTracks.current?.videoTrack) {
      debugLog('ローカルビデオをレンダリング中');
      localTracks.current.videoTrack.play(localVideoRef.current, { fit: "contain" });
    }
  }, [joined]);

  /**
   * 発言者検出の処理
   */
  useEffect(() => {
    if (!joined || !clientRef.current) return;

    const detectSpeaking = () => {
      const users = [...(clientRef.current?.remoteUsers || [])];
      let maxAudioLevel = 0;
      let speakingUid: string | null = null;

      users.forEach((user) => {
        if (user.audioTrack) {
          const audioLevel = user.audioTrack.getVolumeLevel();
          if (audioLevel > maxAudioLevel) {
            maxAudioLevel = audioLevel;
            speakingUid = String(user.uid);
          }
        }
      });

      if (maxAudioLevel > 0.1) {
        setSpeakingUser(speakingUid);
      } else if (!speakingUser && users.length > 0) {
        setSpeakingUser(String(users[0].uid));
      } else if (users.length === 0) {
        setSpeakingUser(null);
      }
    };

    const interval = setInterval(detectSpeaking, 500);
    return () => clearInterval(interval);
  }, [joined, remoteUsers, speakingUser]);

  /**
   * メインビデオの再生
   */
  useEffect(() => {
    if (!mainVideoRef.current || !speakingUser) return;

    const playMainVideo = () => {
      if (!mainVideoRef.current) return;

      const currentMainUser = remoteUsers.find((u) => String(u.uid) === speakingUser);
      if (currentMainUser && currentMainUser.videoTrack) {
        currentMainUser.videoTrack.play(mainVideoRef.current, { fit: "contain" });
      }
    };

    playMainVideo();
  }, [speakingUser, remoteUsers]);

  /**
   * リモートビデオの再生管理
   */
  useEffect(() => {
    remoteUsers.forEach((user) => {
        if (String(user.uid) !== speakingUser) {
        const container = remoteVideoRefs.current.get(String(user.uid));
        if (container) {
            playRemoteVideo(user, container);
        } else {
            console.warn(`ビデオコンテナが見つかりません (UID: ${user.uid})`);
        }
        }
    });

    debugLog('リモートユーザー状態（通常会議）', 
        remoteUsers.map((user) => ({ uid: user.uid, hasVideo: !!user.videoTrack }))
    );
    }, [remoteUsers, speakingUser]);
    
  /**
   * ウィンドウサイズが変更されたときにビデオサイズを再計算
   */
  useEffect(() => {
    if (joined && mainVideoRef.current) {
      updateVideoSize();
    }
  }, [width, height, joined]);

  /**
   * リモート参加者の状態を追跡(ミュート、カメラオフ)
   */
  useEffect(() => {
    if (!joined || !clientRef.current) return;

    // 初期状態の設定
    remoteUsers.forEach(user => {
      setRemoteAudioStates(prev => ({
        ...prev,
        [user.uid]: !!user.audioTrack
      }));
      
      setRemoteVideoStates(prev => ({
        ...prev,
        [user.uid]: !!user.videoTrack
      }));
    });
    
    // getVolumeLevel() ベースの発言者検出に必要（volume-indicator のコールバック引数は SDK 版で異なるため、誤ったハンドラは登録しない）
    clientRef.current.enableAudioVolumeIndicator();
  }, [joined, remoteUsers]);

  /**
   * ページ離脱時の確認処理（F5リフレッシュ対応）
   */
  useEffect(() => {
    // 会議参加中のみイベントリスナーを設定
    if (!joined) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // 会議中の離脱確認（ブラウザ標準メッセージが表示される）
      event.preventDefault();
      event.returnValue = ''; // 空文字でも標準メッセージが表示される
    };

    const handleUnload = () => {
      // ページが実際にアンロードされる時の処理
      if (joined && clientRef.current) {
        // 同期的な退室処理（非同期処理は使用不可）
        try {
          // ローカルトラックの停止・クローズ
          if (localTracks.current?.audioTrack) {
            localTracks.current.audioTrack.setEnabled(false);
            localTracks.current.audioTrack.stop();
            localTracks.current.audioTrack.close();
          }
          if (localTracks.current?.videoTrack) {
            localTracks.current.videoTrack.setEnabled(false);
            localTracks.current.videoTrack.stop();
            localTracks.current.videoTrack.close();
          }
          
          // クライアントから退出（同期処理のみ可能）
          clientRef.current.leave();
          
          console.log('通常会議: 強制退室処理完了');
        } catch (error) {
          console.error('通常会議: 強制退室処理エラー:', error);
        }
      }
    };

    // イベントリスナーを追加
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    // クリーンアップ関数
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, [joined]);

  /**
   * 画面の高さ計算
   */
  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const headerHeight = headerRef.current.getBoundingClientRect().height;
        const newHeight = `calc(100vh - ${headerHeight}px - 48px)`;
        setMainHeight(newHeight);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [joined]);

  /**
   * ビデオサイズ更新関数
   */
  const updateVideoSize = () => {
    if (mainVideoRef.current) {
      const optimalSize = calculateOptimalVideoSize(mainVideoRef.current);
      Object.assign(mainVideoRef.current.style, optimalSize);
    }
  };

  /**
   * 会議参加処理
   */
  const joinMeeting = async () => {
    if (!channelId) {
      message.error('チャンネルIDが指定されていません。');
      return;
    }

    if (uid === null) {
      message.error('UIDが取得できませんでした。');
      return;
    }

    // 既に参加している場合は処理をスキップ
    if (joined || clientRef.current || isJoining) {
      debugLog('既に会議に参加済みです。');
      return;
    }

    setIsJoining(true);
    debugLog('会議に参加中', { channelId, uid, name });
    setJoinTiming({ status: 'running' });
    const joinStartMs = performance.now();
    const logJoinStep = (step: string, stepStartMs?: number) => {
      const totalElapsedMs = Math.round(performance.now() - joinStartMs);
      const stepElapsedMs = stepStartMs == null ? undefined : Math.round(performance.now() - stepStartMs);
      setJoinTiming((prev) => {
        const next = { ...prev, totalElapsedMs };
        if (step === 'agora:client.join:done') next.joinMs = stepElapsedMs;
        if (step === 'firestore:createInvitedUserRecord:done') next.invitedRecordMs = stepElapsedMs;
        if (step === 'agora:createMicrophoneAndCameraTracks:done') next.tracksMs = stepElapsedMs;
        if (step === 'agora:getCameras:done') next.camerasMs = stepElapsedMs;
        if (step === 'agora:publish:done') next.publishMs = stepElapsedMs;
        if (step === 'joinMeeting:success') next.status = 'success';
        if (step === 'joinMeeting:error') next.status = 'error';
        return next;
      });
      debugLog('[join-profiler][standard]', {
        step,
        totalElapsedMs,
        stepElapsedMs,
        channelId,
        uid,
        invitedMode: isInvitedUserMode,
      });
    };
    logJoinStep('joinMeeting:start');

    // クライアント生成
    const client = AgoraRTC.createClient({ 
      mode: AGORA_CONFIG.MODE, 
      codec: AGORA_CONFIG.CODEC 
    });
    clientRef.current = client;

    // ユーザー追加イベントハンドラ
    client.on('user-published', async (user, mediaType) => {
    try {
        await client.subscribe(user, mediaType);
        debugLog(`通常会議: ユーザー ${user.uid} の ${mediaType} をサブスクライブしました`);

        setRemoteUsers((prev) => {
        const filtered = prev.filter((u) => u.uid !== user.uid);
        return [...filtered, user];
        });

        if (mediaType === 'video') {
        setRemoteVideoStates(prev => ({
            ...prev,
            [user.uid]: true
        }));
        
        if (user.videoTrack && String(user.uid) !== speakingUser) {
            const container = remoteVideoRefs.current.get(String(user.uid));
            if (container) {
            playRemoteVideo(user, container);
            } else {
            console.warn(`ビデオコンテナが見つかりません (UID: ${user.uid})`);
            }
        }
        }

        if (mediaType === 'audio') {
        setRemoteAudioStates(prev => ({
            ...prev,
            [user.uid]: true
        }));
        
        user.audioTrack?.play();
        }
    } catch (err) {
        console.error(`通常会議サブスクライブエラー (UID: ${user.uid}):`, err);
    }
    });

    // ユーザーのメディア停止イベントハンドラ
    client.on('user-unpublished', (user, mediaType) => {
      debugLog(`ユーザー ${user.uid} の ${mediaType} が未公開になりました`);
      
      if (mediaType === 'video') {
        setRemoteVideoStates(prev => ({
          ...prev,
          [user.uid]: false
        }));
      } else if (mediaType === 'audio') {
        setRemoteAudioStates(prev => ({
          ...prev,
          [user.uid]: false
        }));
      }

      setRemoteUsers((prev) =>
        prev.map((u) =>
          u.uid === user.uid
            ? { 
                ...u, 
                videoTrack: mediaType === 'video' ? undefined : u.videoTrack,
                audioTrack: mediaType === 'audio' ? undefined : u.audioTrack
              }
            : u
        )
      );      
    });

    // ユーザー退出イベントハンドラ
    client.on('user-left', (user) => {
      debugLog(`ユーザー ${user.uid} が退出しました`);
      setRemoteUsers((prev) => prev.filter((u) => u.uid !== user.uid));
      remoteVideoRefs.current.delete(String(user.uid));

      setRemoteVideoStates(prev => {
        const newState = { ...prev };
        delete newState[user.uid];
        return newState;
      });
      
      setRemoteAudioStates(prev => {
        const newState = { ...prev };
        delete newState[user.uid];
        return newState;
      });      
    });

    try {
      debugLog('チャンネルに接続中', { uid: typeof uid });
      
      // UIDが確実に数値であることを確認
      const numericUid = typeof uid === 'number' ? uid : parseUid(String(uid)).numericUid;

      // 招待参加者の名前は Agora join より先に Firestore へ書く。
      // 後書きだとタブレットが onUserJoined 時点で名前解決できず「参加者」のままになる。
      if (isInvitedUserMode && name) {
        const invitedRecordStepStart = performance.now();
        await createInvitedUserRecord(channelId, name, numericUid);
        logJoinStep('firestore:createInvitedUserRecord:done', invitedRecordStepStart);
      }

      const joinStepStart = performance.now();
      await client.join(AGORA_CONFIG.APP_ID, channelId, AGORA_CONFIG.TOKEN, numericUid);
      debugLog('チャンネル接続成功', { uid: numericUid });
      logJoinStep('agora:client.join:done', joinStepStart);
      
      // トラックの作成と公開
      try {
        const tracksStepStart = performance.now();
        const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();
        localTracks.current = { audioTrack, videoTrack };
        logJoinStep('agora:createMicrophoneAndCameraTracks:done', tracksStepStart);
        const cameraListStepStart = performance.now();
        await loadCameraDevices();
        logJoinStep('agora:getCameras:done', cameraListStepStart);
        
        const tracksToPublish = [audioTrack, videoTrack].filter((track): track is NonNullable<typeof track> => !!track);
        const publishStepStart = performance.now();
        await client.publish(tracksToPublish);
        logJoinStep('agora:publish:done', publishStepStart);
        debugLog('音声・映像トラックを公開しました');
      } catch (trackError) {
        console.error('カメラ/マイクの初期化エラー:', trackError);
        logJoinStep('agora:tracks_or_publish:error');
        message.warning('カメラまたはマイクの初期化に失敗しました。');
        
        if (!localTracks.current) {
          localTracks.current = { audioTrack: null, videoTrack: null };
        }
      }
      
      // 参加成功
      setJoined(true);
      debugLog('会議参加完了');      
      logJoinStep('joinMeeting:success');

      // 参加後にビデオサイズを更新
      setTimeout(updateVideoSize, 500);
    } catch (err) {

       // エラー時にはフラグをリセット（再試行を可能にする）
      setAutoJoinAttempted(false);

      console.error('会議参加エラー:', err);
      message.error('会議への参加に失敗しました。');
      setJoinTiming((prev) => ({
        ...prev,
        status: 'error',
        totalElapsedMs: Math.round(performance.now() - joinStartMs),
      }));
      
      // エラー時のクリーンアップ
      if (localTracks.current?.audioTrack) {
        localTracks.current.audioTrack.close();
      }
      if (localTracks.current?.videoTrack) {
        localTracks.current.videoTrack.close();
      }
      localTracks.current = null;
      clientRef.current = null;
      
      setLeftReason('error');
      setHasLeft(true);
      logJoinStep('joinMeeting:error');
    } finally {
      setIsJoining(false);
    }
  };

  /**
   * ミュート切り替え
   */
  const toggleMute = () => {
    if (localTracks.current?.audioTrack) {
      localTracks.current.audioTrack.setEnabled(isMuted);
      setIsMuted(!isMuted);
    }
  };

  /**
   * ビデオ切り替え
   */
  const toggleVideo = () => {
    if (localTracks.current?.videoTrack) {
      localTracks.current.videoTrack.setEnabled(isVideoOff);
      setIsVideoOff(!isVideoOff);
    }
  };

  /**
   * 利用可能なカメラデバイス一覧を取得
   */
  const loadCameraDevices = async () => {
    try {
      const cameras = await AgoraRTC.getCameras();
      setCameraDeviceIds(cameras.map((camera) => camera.deviceId));
    } catch (error) {
      console.error('カメラデバイスの取得に失敗しました:', error);
    }
  };

  /**
   * カメラ切り替え
   */
  const switchCamera = async () => {
    try {
      const videoTrack = localTracks.current?.videoTrack;
      if (!videoTrack) return;

      let devices = cameraDeviceIds;
      if (devices.length <= 1) {
        const cameras = await AgoraRTC.getCameras();
        devices = cameras.map((camera) => camera.deviceId);
        setCameraDeviceIds(devices);
      }

      if (devices.length <= 1) {
        message.info('切り替え可能なカメラが見つかりません');
        return;
      }

      const nextIndex = (currentCameraIndex + 1) % devices.length;
      await videoTrack.setDevice(devices[nextIndex]);
      setCurrentCameraIndex(nextIndex);
      message.success('カメラを切り替えました');
    } catch (error) {
      console.error('カメラ切り替えに失敗しました:', error);
      message.error('カメラ切り替えに失敗しました');
    }
  };

  /**
   * 退室処理 - beforeunload対応版
   */
  const leaveMeeting = async (isForced: boolean = false) => {
    debugLog('退室処理を開始', { isForced });
    
    if (clientRef.current) {
      try {
        // ===== 1. まずローカルトラックの公開を停止 =====
        if (localTracks.current?.audioTrack || localTracks.current?.videoTrack) {
          const tracksToUnpublish = [];
          if (localTracks.current.audioTrack) tracksToUnpublish.push(localTracks.current.audioTrack);
          if (localTracks.current.videoTrack) tracksToUnpublish.push(localTracks.current.videoTrack);
          
          if (tracksToUnpublish.length > 0) {
            try {
              await clientRef.current.unpublish(tracksToUnpublish);
              debugLog('ローカルトラックの公開を停止しました');
            } catch (unpublishError) {
              if (!isIgnorableLeaveError(unpublishError)) {
                throw unpublishError;
              }
              debugLog('unpublishは実行不要状態のため継続', unpublishError);
            }
          }
        }
        
        // ===== 2. ローカルトラックを停止・クローズ =====
        if (localTracks.current?.audioTrack) {
          localTracks.current.audioTrack.setEnabled(false);
          localTracks.current.audioTrack.stop();
          localTracks.current.audioTrack.close();
        }
        if (localTracks.current?.videoTrack) {
          localTracks.current.videoTrack.setEnabled(false);
          localTracks.current.videoTrack.stop();
          localTracks.current.videoTrack.close();
        }
        
        debugLog('ローカルトラックを停止・クローズしました');
        
        // ===== 3. チャンネルから退出 =====
        try {
          await clientRef.current.leave();
          debugLog('チャンネルから退出完了');
        } catch (leaveError) {
          if (!isIgnorableLeaveError(leaveError)) {
            throw leaveError;
          }
          debugLog('leaveは実行不要状態のため継続', leaveError);
        }

        // 招待ユーザーのみの退室では会議を終了扱いにしない（開設者＝認証ユーザーの退室で記録）
        if (!isInvitedUserMode && channelId) {
          await markChannelClosedInFirestore(channelId, 'agoraitems');
        }
        
        // 強制退室でない場合のみ状態更新
        if (!isForced) {
          setLeftReason('manual');
        }
        
      } catch (err) {
        console.error('退室エラー:', err);
        if (!isForced) {
          message.error('退室処理に失敗しました。');
          setLeftReason('error');
        }
      } finally {
        // ===== 4. 状態を確実にリセット =====
        setJoined(false);
        setRemoteUsers([]);
        setSpeakingUser(null);
        remoteVideoRefs.current.clear();
        setRemoteAudioStates({});
        setRemoteVideoStates({});
        
        // クライアント参照をクリア
        clientRef.current = null;
        localTracks.current = null;
        
        if (!isForced) {
          setHasLeft(true);
        }
        
        debugLog('退室処理完了');
      }
    } else {
      debugLog('クライアントが存在しないため、状態のみリセット');
      if (!isForced) {
        setHasLeft(true);
        setLeftReason('manual');
      }
    }
  };

  // メインエリアのスタイル
  const mainAreaStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: isWideLayout ? 'row' : 'column',
    gap: '8px',
    height: mainHeight,
    minHeight: 0,
    overflow: 'hidden',
    maxWidth: '100%',
    boxSizing: 'border-box',
  };

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
        padding: '24px',
      }}
    >
      {/* ヘッダー部分（会議室名とボタン） */}
      <div ref={headerRef}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <Title level={3} style={{ margin: 0 }}>
            {meetingName || (isInvitedUserMode ? 'テレビ会議' : 'Care Tablet Web')}
          </Title>
          {joined && (
            <Button danger onClick={() => leaveMeeting(false)}> 
              退出
            </Button>
          )}
        </div>

        {joined && (
          <Space style={{ marginTop: 16, marginBottom: 16 }}>
            <Button onClick={toggleMute}>
              {isMuted ? 'ミュート解除' : 'ミュート'}
            </Button>
            <Button onClick={toggleVideo}>
              {isVideoOff ? 'カメラON' : 'カメラOFF'}
            </Button>
            <Button onClick={switchCamera} disabled={cameraDeviceIds.length <= 1}>
              カメラ切替
            </Button>
          </Space>
        )}
        {isTimingDebugMode && joinTiming.status !== 'idle' && (
          <div
            style={{
              marginTop: 8,
              marginBottom: 8,
              padding: '8px 12px',
              borderRadius: 6,
              background: '#fafafa',
              border: '1px solid #d9d9d9',
              fontSize: 12,
              lineHeight: 1.6,
            }}
          >
            <div>
              接続計測: {joinTiming.status === 'running' ? '計測中' : joinTiming.status === 'success' ? '完了' : 'エラー'}
            </div>
            <div>合計: {joinTiming.totalElapsedMs != null ? `${(joinTiming.totalElapsedMs / 1000).toFixed(2)}秒` : '-'}</div>
            <div>Agora Join: {joinTiming.joinMs != null ? `${(joinTiming.joinMs / 1000).toFixed(2)}秒` : '-'}</div>
            <div>招待レコード作成: {joinTiming.invitedRecordMs != null ? `${(joinTiming.invitedRecordMs / 1000).toFixed(2)}秒` : '-'}</div>
            <div>カメラ/マイク取得: {joinTiming.tracksMs != null ? `${(joinTiming.tracksMs / 1000).toFixed(2)}秒` : '-'}</div>
            <div>カメラ一覧取得: {joinTiming.camerasMs != null ? `${(joinTiming.camerasMs / 1000).toFixed(2)}秒` : '-'}</div>
            <div>Publish: {joinTiming.publishMs != null ? `${(joinTiming.publishMs / 1000).toFixed(2)}秒` : '-'}</div>
          </div>
        )}
      </div>

      {hasLeft ? (
        // 退室後の表示
        <Card style={{ maxWidth: 500, margin: '0 auto' }}>
          <div style={{ textAlign: 'center' }}>
            <Title level={3}>
              {leftReason === 'error' ? '⚠️ エラーが発生しました' : '✅ 退室しました'}
            </Title>
            <p>お疲れさまでした。</p>
            {isInvitedUserMode ? (
              <p>ウィンドウを閉じてください。</p>
            ) : (
              <p>再入室する場合には管理画面の「テレビ会議接続」から入室してください。</p>
            )}
            <Space style={{ marginTop: 16 }}>
              <Button 
                type="primary" 
                onClick={() => window.close()}
              >
                ウィンドウを閉じる
              </Button>
            </Space>
          </div>
        </Card>
      ) : (
        <>
          {/* 招待ユーザーの名前入力画面 */}
          {showNameInput && (
            <Card style={{ maxWidth: 400, margin: '0 auto', textAlign: 'center' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Title level={4}>テレビ会議に参加</Title>
                <p>会議に参加する名前を入力してください</p>
                <Input
                  placeholder="あなたの名前"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onPressEnter={handleNameSubmit}
                  disabled={isJoining}
                  maxLength={20}
                />
                <Button 
                  type="primary" 
                  block 
                  onClick={handleNameSubmit}
                  loading={isJoining}
                  disabled={!name.trim() || isJoining}
                >
                  会議に参加
                </Button>
              </Space>
            </Card>
          )}

          {/* 会議準備中の表示 */}
          {!joined && !showNameInput && (
            <Card style={{ maxWidth: 400, margin: '0 auto', textAlign: 'center' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Title level={4}>
                  {initialDataLoaded ? '会議に接続中...' : '会議情報を読み込み中...'}
                </Title>
                <p>参加者: {name}</p>
                {isInvitedUserMode && (
                  <p>招待されたテレビ会議に参加しています</p>
                )}
                <p>しばらくお待ちください</p>
              </Space>
            </Card>
          )} 

          {/* 会議参加中の画面 */}
          {joined && (
            <div style={mainAreaStyle}>
              {/* 左側: メイン画面 (80%) */}
              <div
                style={{
                  flex: isWideLayout ? 8 : 'none', 
                  width: isWideLayout ? 'auto' : '100%',
                  backgroundColor: '#000',
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '0px',
                  overflow: 'hidden',
                }}
              >
                <div
                  ref={mainVideoRef}
                  style={{
                    width: '100%',
                    height: '100%',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    backgroundColor: '#000',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                />
                
                {/* メイン画面に参加者がいない場合の表示 */}
                {!speakingUser && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#000',
                      color: '#fff',
                      fontSize: '24px',
                    }}
                  >
                    メイン画面: 参加者なし
                  </div>
                )}
                
                {/* メイン画面に表示中の参加者名オーバーレイ */}
                {speakingUser && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '10px',
                      left: '10px',
                      padding: '8px 12px',
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      color: '#fff',
                      borderRadius: '6px',
                      fontSize: '24px',
                      fontWeight: 'bold',
                      pointerEvents: 'none',
                      zIndex: 10,
                    }}
                  >
                    {getParticipantName(speakingUser, participants)}
                  </div>
                )}
                
                {/* メイン表示者の音声状態オーバーレイ */}
                {speakingUser && !remoteAudioStates[speakingUser] && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '10px',
                      right: '10px',
                      padding: '6px 10px',
                      backgroundColor: 'rgba(255, 0, 0, 0.8)',
                      color: '#fff',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      pointerEvents: 'none',
                      zIndex: 10,
                    }}
                  >
                    🔇 音声オフ
                  </div>
                )}
                                
                {/* メイン表示者がカメラオフの場合の表示 */}
                {speakingUser && !remoteVideoStates[speakingUser] && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#111',
                      color: '#fff',
                    }}
                  >
                    <img 
                      src={cameraOffIcon} 
                      alt="カメラオフ" 
                      style={{ 
                        ...getCameraOffIconSize(true), // 🔧 修正: レスポンシブサイズ適用
                        marginBottom: '16px' 
                      }}
                    /> 
                     {/* 
                    <div style={{ fontSize: '24px' }}>
                      {getParticipantName(speakingUser, participants)}
                      {!remoteAudioStates[speakingUser] && (
                        <span style={{ color: 'red', marginLeft: '10px' }}>（音声オフ）</span>
                      )}
                    </div>
                    */}
                  </div>
                )}            
              </div>

              {/* 右側: 参加者一覧と未参加者一覧 (20%) */}
              <div
                style={{
                  flex: isWideLayout ? 2 : 1,
                  width: isWideLayout ? 'auto' : '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  padding: '4px',
                  backgroundColor: '#f0f0f0',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                }}
              >
                {/* 参加者セクション */}
                <div>
                  <Title level={5} style={{ 
                    margin: '0px 0',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    overflow: 'hidden',
                  }}>
                    参加者
                  </Title>
                  
                  {/* 自分 (ローカルユーザー) */}
                  <Card
                    title={`自分 (${name})`}
                    style={{
                      marginBottom: '2px',
                      width: isMobilePortrait ? '50%' : '100%',
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                      wordBreak: 'break-word',
                    }}
                    bodyStyle={{ padding: '4px' }}
                  >
                    <div
                      ref={localVideoRef}
                      style={{
                        width: '100%', 
                        aspectRatio: '16 / 9',
                        backgroundColor: '#111',
                        position: 'relative',
                      }}
                    >
                      {/* ビデオがオフの場合の表示 */}
                      {isVideoOff && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#111',
                            color: '#fff',
                          }}
                        >
                          <img src={cameraOffIcon} alt="カメラオフ" style={{ 
                              ...getCameraOffIconSize(false) // 🔧 修正: レスポンシブサイズ適用
                            }} 
                          />
                        </div>
                      )}
                      
                      {/* 音声オフの場合はオーバーレイ表示 */}
                      {isMuted && (
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '5px',
                            left: '5px',
                            padding: '4px 8px',
                            backgroundColor: 'rgba(255, 0, 0, 0.7)',
                            color: '#fff',
                            fontSize: '12px',
                            borderRadius: '4px',
                            pointerEvents: 'none',
                            zIndex: 2,
                          }}
                        >
                          音声オフ
                        </div>
                      )}
                    </div>
                  </Card>
                  
                  {/* リモートユーザー */}
                  {remoteUsers.map((user) => (
                    <Card
                      key={user.uid}
                      title={getParticipantName(String(user.uid), participants)}
                      style={{
                        marginBottom: '2px',
                        width: isMobilePortrait ? '50%' : '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        wordBreak: 'break-word',
                      }}
                      bodyStyle={{ padding: '4px' }}
                    >
                      <div style={{ position: 'relative' }}>
                        {/* ビデオコンテナ */}
                        <div
                        ref={(el) => {
                            if (el) {
                            remoteVideoRefs.current.set(String(user.uid), el);
                            if (user.videoTrack && String(user.uid) !== speakingUser) {
                                playRemoteVideo(user, el);
                            }
                            }
                        }}
                        id={`remote-${user.uid}`}
                        style={{
                            width: '100%',
                            aspectRatio: '16 / 9',
                            backgroundColor: '#111',
                            position: 'relative',
                        }}
                        />                      

                        {/* メインに表示中のオーバーレイ */}
                        {String(user.uid) === speakingUser && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              padding: '4px 8px',
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                              color: '#fff',
                              fontSize: '12px',
                              borderRadius: '4px',
                              pointerEvents: 'none',
                            }}
                          >
                            メイン画面に<br />表示中
                          </div>
                        )}
                        
                        {/* 音声オフの表示 */}
                        {!remoteAudioStates[user.uid] && (
                          <div
                            style={{
                              position: 'absolute',
                              bottom: '5px',
                              left: '5px',
                              padding: '4px 8px',
                              backgroundColor: 'rgba(255, 0, 0, 0.7)',
                              color: '#fff',
                              fontSize: '12px',
                              borderRadius: '4px',
                              pointerEvents: 'none',
                              zIndex: 2,
                            }}
                          >
                            音声オフ
                          </div>
                        )}
                        
                        {/* カメラオフの表示 */}
                        {!remoteVideoStates[user.uid] && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#111',
                              color: '#fff',
                              pointerEvents: 'none',
                            }}
                          >
                            <img src={cameraOffIcon} alt="カメラオフ" style={{ 
                                ...getCameraOffIconSize(false) // 🔧 修正: レスポンシブサイズ適用
                              }} 
                            />
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>

                {/* 未参加者セクション（実在参加者のみ表示。uid:0 のプレースホルダーは除外） */}
                {(() => {
                  const nonParticipants = getNonParticipants(participants, remoteUsers).filter((p) => p.uid > 0);
                  if (nonParticipants.length === 0) {
                    return null;
                  }
                  return (
                    <div>
                      <Title level={5} style={{ margin: '0px 0' }}>
                        未参加者
                      </Title>
                      {nonParticipants.map((participant) => (
                        <div
                          key={participant.uid}
                          style={{
                            width: isMobilePortrait ? '50%' : '100%',
                            maxWidth: '100%',
                            padding: '8px',
                            backgroundColor: '#fff',
                            borderRadius: '4px',
                            marginBottom: '2px',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                            boxSizing: 'border-box',
                            wordBreak: 'break-word',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {participant.name} (UID: {participant.uid})
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AgoraStandardMeeting;